/**
 * Hook for enhanced tooltip behavior
 *
 * Provides controlled tooltip state with:
 * - Auto-dismiss after delay (all platforms — safety net for stuck tooltips)
 * - Click-away dismissal on mobile
 * - Tap-to-open on touch devices
 *
 * On desktop, Chakra's onOpen/onClose still fire on hover, but the controlled
 * state lets us force-close after a timeout (e.g. when drag suppresses mouseleave).
 */

import { useMediaQuery } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { MOBILE_VIEWPORT_QUERY } from '../../utils/bowerClient';

// `useMobileDetection().isTouchDevice || .isMobile`, from two media queries
// rather than that hook. It subscribes to every Chakra breakpoint, and every
// Tooltip instance re-rendered on each one a desktop window resize crossed. A
// desktop UA asks a query that never matches, so it holds no width subscription.
const MOBILE_UA = /iphone|ipad|ipod|android/i.test(navigator.userAgent);
const TOUCH_QUERY = '(hover: none) and (pointer: coarse)';
const SMALL_QUERY = MOBILE_UA ? MOBILE_VIEWPORT_QUERY : 'not all';

interface UseTooltipOptions {
  /**
   * Auto-dismiss delay in milliseconds
   * Default: 3000ms (3 seconds)
   */
  autoDismissDelay?: number;
  /**
   * Whether to enable enhanced behavior
   * If false, returns undefined (use default Chakra UI behavior)
   */
  enabled?: boolean;
}

interface UseTooltipReturn {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  openDelay: number;
  closeDelay: number;
  /** True when running on a touch/mobile device */
  isMobileDevice: boolean;
}

/**
 * Hook that provides enhanced tooltip props on all platforms.
 *
 * Returns controlled isOpen/onOpen/onClose so Chakra still responds to hover
 * but we can force-close after a timeout (prevents stuck tooltips during drag,
 * focus loss, etc.).
 */
export function useMobileTooltip(options: UseTooltipOptions = {}): UseTooltipReturn | undefined {
  const { autoDismissDelay = 3000, enabled = true } = options;
  const [isTouchDevice, isSmallMobile] = useMediaQuery([TOUCH_QUERY, SMALL_QUERY]);
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMobileDevice = (isTouchDevice || isSmallMobile) && enabled;

  // Auto-dismiss on ALL platforms when tooltip is open
  useEffect(() => {
    if (!enabled || !isOpen) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, autoDismissDelay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [enabled, isOpen, autoDismissDelay]);

  // Click-away dismissal on mobile
  useEffect(() => {
    if (!isMobileDevice || !isOpen) return;

    const handleClickAway = (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement;

      // Don't dismiss if clicking on a tooltip trigger
      const tooltipTrigger = target.closest('[data-tooltip-trigger]');
      if (tooltipTrigger) return;

      // Don't dismiss if clicking on the tooltip itself
      const tooltipElement = document.querySelector('[role="tooltip"], [data-popper-placement]');
      if (tooltipElement?.contains(target)) return;

      setIsOpen(false);
    };

    // Small delay to allow click events to propagate first
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickAway, true);
      document.addEventListener('touchstart', handleClickAway, true);
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickAway, true);
      document.removeEventListener('touchstart', handleClickAway, true);
    };
  }, [isMobileDevice, isOpen]);

  if (!enabled) return;

  return {
    isOpen,
    onOpen: () => setIsOpen(true),
    onClose: () => setIsOpen(false),
    openDelay: isMobileDevice ? 0 : 300,
    closeDelay: isMobileDevice ? 0 : 100,
    isMobileDevice,
  };
}
